import React from 'react';
import { Smartphone, Bell, Heart, Star } from 'lucide-react';
import AppStoreBadges from './AppStoreBadges';
import { APP_STORE } from '../config/appStoreConfig';

/**
 * AppDownloadResultsCard — the highest-leverage app CTA on the site.
 * Rendered immediately after the 4 gift recommendations on the results page,
 * at the moment of revealed value (post-conversion). Benchmark data: post-value
 * banner installs convert ~5× higher than landing-page installs.
 */
const AppDownloadResultsCard = () => {
  return (
    <section
      aria-labelledby="app-download-heading"
      className="max-w-3xl mx-auto my-12 px-6 py-8 sm:px-10 sm:py-10 bg-gradient-to-br from-blue-50 via-white to-orange-50 rounded-3xl shadow-lg border border-blue-100"
    >
      <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
        {/* Left: icon + copy */}
        <div className="flex-1 text-center lg:text-left">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-4 shadow-md">
            <Smartphone className="text-white" size={28} />
          </div>

          <h3
            id="app-download-heading"
            className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2"
          >
            Save your picks. Get deal alerts.
          </h3>
          <p className="text-gray-600 mb-4 text-sm sm:text-base">
            Take Pickify with you. Save your gift ideas, get notified before prices rise, and re-quiz in 10 seconds.
          </p>

          {/* Benefit bullets */}
          <ul className="space-y-2 text-sm text-gray-700 mb-6 max-w-md mx-auto lg:mx-0">
            <li className="flex items-start gap-2">
              <Heart className="text-pink-500 flex-shrink-0 mt-0.5" size={16} />
              <span>Save your gift picks across devices</span>
            </li>
            <li className="flex items-start gap-2">
              <Bell className="text-orange-500 flex-shrink-0 mt-0.5" size={16} />
              <span>Get notified when prices drop on saved items</span>
            </li>
            <li className="flex items-start gap-2">
              <Star className="text-yellow-400 fill-yellow-400 flex-shrink-0 mt-0.5" size={16} />
              <span>Exclusive deals only available in the app</span>
            </li>
          </ul>

          {/* Rating row */}
          <div className="flex items-center justify-center lg:justify-start gap-2 mb-5">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} size={16} className="text-yellow-400 fill-yellow-400" />
              ))}
            </div>
            <span className="text-sm font-semibold text-gray-700">{APP_STORE.meta.rating}</span>
            <span className="text-sm text-gray-500">· {APP_STORE.meta.ratingCount} gifts found</span>
          </div>

          {/* Badges */}
          <div className="flex justify-center lg:justify-start">
            <AppStoreBadges placement="results_card" variant="auto" height={48} />
          </div>
        </div>

        {/* Right: phone mockup (decorative; hidden on small screens to save space) */}
        <div className="hidden lg:block flex-shrink-0">
          <div className="relative">
            <div className="w-44 h-80 bg-gradient-to-br from-gray-800 to-gray-900 rounded-[2.2rem] p-2 shadow-2xl">
              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-orange-500 rounded-[1.8rem] overflow-hidden relative flex flex-col items-center justify-center text-white">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-gray-900 rounded-b-xl" />
                <Smartphone className="opacity-90 mb-3" size={48} />
                <p className="font-bold text-xl">Pickify</p>
                <p className="text-xs opacity-90 mt-1">{APP_STORE.meta.tagline}</p>
                <div className="flex mt-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} size={10} className="text-yellow-300 fill-yellow-300" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppDownloadResultsCard;
